/**
 * FO-151 (batch-30) — vaklocatie (stelling / plank / vak) binnen een magazijn.
 *
 * Eén artikel-op-magazijnlocatie (VoorraadSaldo) of uniek exemplaar
 * (VoorraadUniekItem) kan een vaklocatie hebben: stelling, optioneel plank,
 * optioneel vak. Weergave altijd als `stelling[-plank[-vak]]` met streepjes,
 * bv. `3-1-3` (stelling 3, plank 1, vak 3), `3-1` of `3` — gelijk aan de
 * fysieke stellingetiketten in het magazijn (Burak 14-08; was punt-notatie).
 *
 * Opslag is GESTRUCTUREERD (drie nullable ints in de DB) — de code is puur
 * afgeleid. Dit bestand is de enige waarheid voor formatteren, parsen
 * (sneltoets-invoer "3-1-3") en de looproute-sortering (stelling → plank →
 * vak, lege vaklocaties achteraan). Pure functies; gedeeld door API, UI en
 * de QR-/tellijst-PDF-renderer.
 *
 * Hiërarchie-regel: vak vereist plank, plank vereist stelling. Waarden zijn
 * kleine gehele getallen; stelling >= 1, plank/vak >= 0 (plank 0 = onderste
 * bord / grijpvoorraad, zie FO-151 §10 voorbeeld "9.0.1").
 */

export interface Vaklocatie {
  stelling: number | null
  plank: number | null
  vak: number | null
}

/** Bovengrens per veld — vangt tikfouten ("33333") af; magazijnen zijn klein. */
export const VAKLOCATIE_MAX = 999

/** Lege vaklocatie (alles null) — handig als default/wis-waarde. */
export const LEGE_VAKLOCATIE: Vaklocatie = { stelling: null, plank: null, vak: null }

function isGeldigVeld(n: number | null, min: number): n is number {
  return n != null && Number.isInteger(n) && n >= min && n <= VAKLOCATIE_MAX
}

/**
 * Valideer de hiërarchie + waardebereiken van een vaklocatie.
 * Geldig is: alles null (geen vaklocatie), alleen stelling, stelling+plank,
 * of stelling+plank+vak. Een plank zonder stelling of vak zonder plank is
 * ongeldig, net als niet-gehele of te grote getallen.
 */
export function isGeldigeVaklocatie(v: Vaklocatie): boolean {
  const { stelling, plank, vak } = v
  if (stelling == null && plank == null && vak == null) return true
  if (!isGeldigVeld(stelling, 1)) return false
  if (plank == null) return vak == null
  if (!isGeldigVeld(plank, 0)) return false
  if (vak == null) return true
  return isGeldigVeld(vak, 0)
}

/**
 * Formatteer een vaklocatie als code: `3-1-3`, `3-1`, `3` of `null` (geen
 * stelling). Een hiërarchie-gat (vak zonder plank) wordt defensief afgekapt
 * bij het eerste ontbrekende niveau — de validatie hoort dat al te weigeren.
 */
export function formatVaklocatie(v: Vaklocatie | null | undefined): string | null {
  if (!v || v.stelling == null) return null
  const delen: number[] = [v.stelling]
  if (v.plank != null) {
    delen.push(v.plank)
    if (v.vak != null) delen.push(v.vak)
  }
  return delen.join('-')
}

/**
 * Parse een sneltoets-invoer ("3-1-3", "3.1.3", "3,1", " 3 ") naar de drie
 * velden. Retourneert `null` bij lege input of een niet-parsebare/ongeldige
 * waarde (te veel delen, geen getallen, hiërarchie/bereik fout).
 */
export function parseVaklocatie(input: string | null | undefined): Vaklocatie | null {
  if (input == null) return null
  const schoon = input.trim()
  if (schoon === '') return null
  // Moet met een cijfer beginnen en eindigen — een leidende "-" is een
  // negatief getal (ongeldig), geen scheidingsteken.
  if (!/^\d/.test(schoon) || !/\d$/.test(schoon)) return null
  const delen = schoon.split(/[.,\-\s]+/).filter((d) => d !== '')
  if (delen.length < 1 || delen.length > 3) return null
  const nummers: number[] = []
  for (const deel of delen) {
    if (!/^\d+$/.test(deel)) return null
    nummers.push(Number.parseInt(deel, 10))
  }
  const kandidaat: Vaklocatie = {
    stelling: nummers[0] ?? null,
    plank: nummers[1] ?? null,
    vak: nummers[2] ?? null,
  }
  return isGeldigeVaklocatie(kandidaat) ? kandidaat : null
}

/**
 * Looproute-vergelijker: sorteer op stelling → plank → vak, oplopend.
 * Lege vaklocaties (geen stelling) komen ACHTERAAN; binnen dezelfde stelling
 * komt "3" (geen plank) vóór "3-0" en "3-1" — de teller pakt eerst de
 * stelling-brede plek en loopt dan de planken af.
 */
export function compareVaklocatie(
  a: Vaklocatie | null | undefined,
  b: Vaklocatie | null | undefined,
): number {
  const sa = a?.stelling ?? null
  const sb = b?.stelling ?? null
  if (sa == null && sb == null) return 0
  if (sa == null) return 1
  if (sb == null) return -1
  if (sa !== sb) return sa - sb

  const pa = a?.plank ?? null
  const pb = b?.plank ?? null
  if (pa == null && pb == null) return 0
  if (pa == null) return -1
  if (pb == null) return 1
  if (pa !== pb) return pa - pb

  const va = a?.vak ?? null
  const vb = b?.vak ?? null
  if (va == null && vb == null) return 0
  if (va == null) return -1
  if (vb == null) return 1
  return va - vb
}

/** True wanneer beide vaklocaties (incl. leeg) exact gelijk zijn. */
export function isGelijkeVaklocatie(a: Vaklocatie, b: Vaklocatie): boolean {
  return a.stelling === b.stelling && a.plank === b.plank && a.vak === b.vak
}
